import type { BoxProps } from "@mantine/core";
import { Box } from "@mantine/core";
import type { FC } from "react";
import { useMemo } from "react";

type Props = BoxProps & {
  color?: string;
  animated?: boolean;
  animationDuration?: string;
};

export const NullLoader: FC<Props> = ({
  color = "var(--mantine-color-text)",
  animated,
  animationDuration = "2s",
  ...props
}) => {
  const fill = color;
  const filterId = useMemo(
    () => `null-logo-static-${Math.random().toString(36).slice(2, 11)}`,
    [],
  );

  return (
    <Box {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 446 236"
        style={{ width: "100%" }}
      >
        <defs>
          {animated && (
            <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.02"
                numOctaves="1"
                result="turbulence"
              >
                <animate
                  attributeName="baseFrequency"
                  values="0.8;0.02;0.8"
                  dur={animationDuration}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="seed"
                  from="0"
                  to="100"
                  dur="0.1s"
                  repeatCount="indefinite"
                />
              </feTurbulence>

              {/* Create noise mask for color mixing */}
              <feColorMatrix
                in="turbulence"
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 20 -10"
                result="noiseMask"
              />

              {/* Displacement effect */}
              <feDisplacementMap
                in="SourceGraphic"
                in2="turbulence"
                scale="80"
                xChannelSelector="R"
                yChannelSelector="G"
                result="displaced"
              >
                <animate
                  attributeName="scale"
                  values="80;0;80"
                  dur={animationDuration}
                  repeatCount="indefinite"
                />
              </feDisplacementMap>

              {/* Mix in opposite color */}
              <feFlood
                floodColor="var(--mantine-color-body)"
                result="bgColor"
              />
              <feComposite
                in="bgColor"
                in2="noiseMask"
                operator="in"
                result="noiseBg"
              />

              <feBlend
                in="displaced"
                in2="noiseBg"
                mode="normal"
                result="final"
              >
                <animate
                  attributeName="mode"
                  values="normal;normal;normal"
                  dur={animationDuration}
                  repeatCount="indefinite"
                />
              </feBlend>
            </filter>
          )}
        </defs>

        <g filter={animated ? `url(#${filterId})` : undefined}>
          <path
            d="M130.380005,46.089996 C155.730005,46.269996,170.790005,66.819996,164.040005,92.189995 L164.010005,92.189995 L148.370004,150.949993 C145.050004,163.439993,152.500005,173.569994,165.020004,173.569994 L187.580003,173.569994 L209.240003,92.189997 C222.510004,42.329996,193.150003,1.879999,143.590002,1.049997 L143.610002,0.969997 L63.169999,0.969997 L51.159999,46.069996 L51.159999,46.069996 L51.159999,46.069996 L0.619998,235.969990 L23.239999,235.969990 C35.729999,235.969990,48.539998,225.869989,51.860000,213.409990 L96.389999,46.089983 L130.380005,46.089996 Z "
            fill={fill}
          >
            {animated && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-2,1; 1,-2; -1,2; 2,-1; 0,0; 0,0"
                keyTimes="0; 0.1; 0.2; 0.3; 0.5; 1"
                dur="0.12s"
                repeatCount="indefinite"
              />
            )}
          </path>
          <path
            d="M388.170013,128.789993 L416.200014,23.539993 C419.520014,11.079993,412.080014,0.979994,399.590014,0.979994 L376.980013,0.979994 L330.930014,173.899992 L376.160013,173.899992 L438.650015,173.919992 L444.660015,151.349992 C447.970015,138.899992,440.540015,128.799993,428.060015,128.789993 L388.180014,128.789993 L388.170013,128.789993 Z "
            fill={fill}
          >
            {animated && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values="1,-1; -2,2; 2,-2; -1,1; 0,0; 0,0"
                keyTimes="0; 0.1; 0.2; 0.3; 0.5; 1"
                dur="0.11s"
                repeatCount="indefinite"
              />
            )}
          </path>
          <path
            d="M343.100006,63.050003 L343.100006,63.050003 L353.620007,23.540005 C356.940006,11.080005,349.500007,0.980005,337.010006,0.980005 L314.400005,0.980005 L275.980007,145.250010 L275.980007,145.250010 L251.820007,235.980013 L422.130005,235.980013 L428.130005,213.430014 C431.450005,200.970014,424.010005,190.870014,411.520004,190.870014 L309.060005,190.870014 L343.100006,63.060017 L343.100006,63.050003 Z "
            fill={fill}
          >
            {animated && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values="2,2; -1,-1; 1,2; -2,-2; 0,0; 0,0"
                keyTimes="0; 0.1; 0.2; 0.3; 0.5; 1"
                dur="0.13s"
                repeatCount="indefinite"
              />
            )}
          </path>
          <path
            d="M245.529999,190.889999 L245.529999,190.869999 L296.070000,0.970005 L273.449999,0.970005 C260.959999,0.970005,248.150000,11.070006,244.829998,23.530005 L200.299999,190.850012 L166.309998,190.850012 C140.959997,190.670012,125.899998,170.120013,132.649998,144.750014 L132.679998,144.750014 L148.319998,85.990015 C151.639998,73.500016,144.189998,63.370015,131.669998,63.370015 L109.109999,63.370015 L87.449999,144.750012 L87.449999,144.750012 C74.179999,194.610013,103.539999,235.060009,153.110003,235.890011 L153.090003,235.970011 L233.530005,235.970011 L245.530005,190.890009 C245.530005,190.890009,245.540005,190.880009,245.540005,190.870009 L245.540005,190.890009 L245.529999,190.889999 Z "
            fill={fill}
          >
            {animated && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-1,2; 2,-1; -2,1; 1,-2; 0,0; 0,0"
                keyTimes="0; 0.1; 0.2; 0.3; 0.5; 1"
                dur="0.09s"
                repeatCount="indefinite"
              />
            )}
          </path>
        </g>
      </svg>
    </Box>
  );
};
